import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  Typography,
} from '@mui/material';
import { useCallback, useState } from 'react';

import { useModal, useToastr } from '@farmshare/ui-components';

import {
  useCustomerDeleteMutation,
  useCustomerDeletePreviewQuery,
} from '../../../lib/graphql';

import { candidateLabel, type CustomerSummary } from './merge-wizard-types';

export interface DeleteCustomerModalOptions {
  vendorId: string | undefined;
  customer: CustomerSummary;
  onDeleted: () => void;
  // Invoked from the blocked state to route into the merge wizard with this
  // customer pre-populated.
  onMergeInstead: (customer: CustomerSummary) => void;
}

/**
 * Body of the delete modal: the pre-flight check and the actions it unlocks.
 *
 * The actions live in here rather than in the modal's `footer` because which
 * buttons appear depends on the pre-flight query, and that query needs a hook.
 * A `footer` render function only re-runs when `ModalInner` itself re-renders,
 * which for a non-Formik modal it never does — so a footer built from this
 * state would be frozen on its first (still-loading) result. The content is two
 * lines at most, so nothing of consequence scrolls past the buttons.
 * @param root0 - Props.
 * @param root0.vendorId - Vendor the customer is being removed from.
 * @param root0.customer - The customer under consideration.
 * @param root0.onDeleted - Called after a successful delete.
 * @param root0.onMergeInstead - Called to hand off to the merge wizard.
 * @param root0.onClose - Closes the modal.
 * @param root0.setBusy - Tells the modal a delete is in flight, so it can't be closed over.
 * @returns The modal body.
 */
function DeleteCustomerBody({
  vendorId,
  customer,
  onDeleted,
  onMergeInstead,
  onClose,
  setBusy,
}: DeleteCustomerModalOptions & {
  onClose: () => void;
  setBusy: (busy: boolean) => void;
}) {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { push } = useToastr();

  const { data, loading: previewLoading } = useCustomerDeletePreviewQuery({
    variables: { userId: customer._id, vendorId: vendorId as string },
    skip: !vendorId,
    fetchPolicy: 'network-only',
  });
  const preview = data?.customerDeletePreview;

  const [runDelete, { loading: deleting }] = useCustomerDeleteMutation();

  const handleDelete = async () => {
    if (!vendorId) return;
    setErrorMessage(null);
    // The mutation runs in here, where the modal can't see it — so without
    // this the X, the backdrop and Escape would all close the dialog over a
    // delete that then finishes (or fails) with nothing on screen to say so.
    // The old dialog refused to close while `deleting`; this is that guard.
    setBusy(true);
    try {
      await runDelete({ variables: { userId: customer._id, vendorId } });
    } catch (e) {
      setErrorMessage(e instanceof Error ? e.message : 'Delete failed');
      return;
    } finally {
      // Released before `onDeleted` closes the modal, or the close is refused.
      setBusy(false);
    }
    // A destructive change that used to finish in silence: say who went and
    // what that means, in the same terms as the dialog's warning.
    push({
      title: 'Customer deleted',
      body: preview?.multiVendor
        ? `${candidateLabel(customer)} was removed from your customers. They can still sign in for their other processors.`
        : `${candidateLabel(customer)} was removed, and their login (if any) disabled.`,
      severity: 'success',
      delay: 6000,
    });
    onDeleted();
  };

  const blocked = preview && !preview.canDelete;
  const multiVendor = !!preview?.multiVendor;
  // Merge is single-vendor only, so only offer it for single-vendor customers
  // blocked by associated records.
  const canMergeInstead = blocked && !multiVendor;

  return (
    <>
      {previewLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Checking associated records…
          </Typography>
        </Box>
      )}

      {!previewLoading && preview?.canDelete && !multiVendor && (
        <Typography variant="body2" sx={{ pt: 1 }}>
          This removes the customer (and disables their login, if any) and can
          be reversed by contacting engineering.
        </Typography>
      )}

      {!previewLoading && preview?.canDelete && multiVendor && (
        <Typography variant="body2" sx={{ pt: 1 }}>
          This removes the customer from this processor. They have at least one
          relationship with another processor. They will still be visible to
          those processors and able to log in.
        </Typography>
      )}

      {!previewLoading && canMergeInstead && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          <strong>{candidateLabel(customer)}</strong> has{' '}
          {preview?.associatedRecordCount} associated record
          {preview?.associatedRecordCount === 1 ? '' : 's'} and cannot be
          deleted directly. Merge them into another customer instead.
        </Alert>
      )}

      {!previewLoading && blocked && multiVendor && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          <strong>{candidateLabel(customer)}</strong> has{' '}
          {preview?.associatedRecordCount} associated record
          {preview?.associatedRecordCount === 1 ? '' : 's'} with this processor
          and can&apos;t be removed here yet. Contact engineering for help.
        </Alert>
      )}

      {errorMessage && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {errorMessage}
        </Alert>
      )}

      <DialogActions sx={{ px: 0, pb: 0, pt: 2 }}>
        <Button onClick={onClose} disabled={deleting}>
          Cancel
        </Button>
        {canMergeInstead && (
          <Button variant="contained" onClick={() => onMergeInstead(customer)}>
            Merge into another customer
          </Button>
        )}
        {!blocked && (
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={previewLoading || deleting || !preview?.canDelete}
          >
            {deleting ? 'Removing…' : 'Remove'}
          </Button>
        )}
      </DialogActions>
    </>
  );
}

/**
 * Returns a function that opens the delete-customer modal.
 *
 * The modal pre-flights associated records: zero → offer the delete, otherwise
 * → offer a hand-off into the merge wizard.
 * @returns A callback that opens the modal.
 */
export function useDeleteCustomerModal() {
  const { info } = useModal();

  return useCallback(
    (options: DeleteCustomerModalOptions) => {
      info({
        type: 'info',
        // The old dialog swapped its title on the pre-flight result. The title
        // is fixed here because it is set when the modal opens, before that
        // query has run — the body says which case we are in anyway.
        title: `Delete ${candidateLabel(options.customer)}`,
        size: 'sm',
        // Short, confirm-shaped content, so keep it a centered card on a
        // phone. That has to be asked for: `info` defaults to fullscreen there,
        // being mostly used for content-heavy surfaces.
        fullscreenOnMobile: false,
        footer: null,
        body: (modalProps) => (
          <DeleteCustomerBody
            {...options}
            onClose={() => modalProps.onClose?.()}
            setBusy={(busy) => modalProps.setBusy?.(busy)}
            onDeleted={() => {
              modalProps.onClose?.();
              options.onDeleted();
            }}
            onMergeInstead={(customer) => {
              // No explicit close: opening the merge wizard is a multiStep
              // modal, and the provider clears the regular modal when one
              // opens. Closing here as well would double-fire the exit.
              options.onMergeInstead(customer);
            }}
          />
        ),
      });
    },
    [info],
  );
}
